import { useState, useRef, useEffect } from "react";
import { ChevronDown, Building2, Plus, Check } from "lucide-react";
import { useWorkspace } from "../contexts/WorkspaceContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function WorkspaceSwitcher() {
  const { workspaces, active, switchWorkspace, refresh } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setCreating(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const submitCreate = async () => {
    const name = newName.trim();
    if (name.length < 2) {
      toast.error("Workspace name must be at least 2 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/workspaces", { name });
      toast.success(`Workspace "${data.name}" created`);
      await refresh();
      switchWorkspace(data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="workspace-switcher-btn"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-zinc-300 hover:border-[#002FA7] hover:bg-zinc-50 transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Building2 className="w-4 h-4 text-zinc-600 shrink-0" strokeWidth={2} />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 leading-none">Workspace</div>
            <div className="text-sm font-bold truncate mt-0.5">{active?.name || "—"}</div>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="workspace-switcher-menu"
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-zinc-300 shadow-lg max-h-[60vh] overflow-auto"
        >
          {workspaces.map((w) => (
            <button
              key={w.id}
              data-testid={`workspace-option-${w.id}`}
              onClick={() => switchWorkspace(w.id)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-zinc-100 transition-colors ${
                w.id === active?.id ? "bg-zinc-50" : ""
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{w.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">
                  {w.role}
                  {w.is_owner ? " · owner" : ""}
                </div>
              </div>
              {w.id === active?.id && <Check className="w-4 h-4 text-[#002FA7] shrink-0" strokeWidth={2.5} />}
            </button>
          ))}
          <div className="border-t border-zinc-200">
            {creating ? (
              <div className="p-2 space-y-2">
                <input
                  data-testid="new-workspace-name-input"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Workspace name"
                  autoFocus
                  className="w-full px-2 py-1.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitCreate();
                    if (e.key === "Escape") { setCreating(false); setNewName(""); }
                  }}
                />
                <div className="flex gap-1.5">
                  <button
                    data-testid="new-workspace-create-btn"
                    disabled={busy}
                    onClick={submitCreate}
                    className="flex-1 px-2 py-1.5 bg-[#002FA7] text-white text-xs font-semibold uppercase tracking-wider disabled:opacity-50"
                  >
                    {busy ? "Creating…" : "Create"}
                  </button>
                  <button
                    onClick={() => { setCreating(false); setNewName(""); }}
                    className="px-2 py-1.5 border border-zinc-300 text-xs font-semibold uppercase tracking-wider hover:bg-zinc-100"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                data-testid="new-workspace-btn"
                onClick={() => setCreating(true)}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-zinc-100 transition-colors text-sm font-semibold text-[#002FA7]"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5} />
                New workspace
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
